import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { greys, onwardNavy } from '@/styles/colors';
import { FONT, SectionTitle, Empty, Pill, Card, PhotoGrid, ACCENT, SOFT } from './detailTabBlocks';

// Redesigned Items/Docs tab for the Accessorial Audit rail. Mirrors every data point
// rendered by Accessorials/OrderInfoSection/ItemsAndDocs (items, haulaway, warehouse /
// BOL / waybill / pickup-notification / POD / delivery photos, customer signature) but
// in a compact card layout. Self-contained so the shared component is left untouched.

const metaLine = css`
    font-family: ${FONT};
    font-weight: 500;
    font-size: 11px;
    color: ${greys[1]};
`;

function ItemMeta({ label, value }) {
    return (
        <div
            css={css`
                display: flex;
                justify-content: space-between;
                gap: 10px;
                padding: 3px 0;
            `}
        >
            <span css={metaLine}>{label}</span>
            <span
                css={css`
                    font-family: ${FONT};
                    font-weight: 600;
                    font-size: 11.5px;
                    color: ${greys[3]};
                    text-align: right;
                `}
            >
                {value}
            </span>
        </div>
    );
}

function PhotoSection({ title, urls, keyPrefix }) {
    const count = urls?.length || 0;
    return (
        <>
            <SectionTitle count={count || null}>{title}</SectionTitle>
            <PhotoGrid urls={urls} keyPrefix={keyPrefix} />
        </>
    );
}

export default function DetailTabItemsAndDocs({ order }) {
    const items = order?.itemsByOrderId || [];
    const haulaway = order?.haulaway_items || [];

    // Distinct item-type counts (raw item_type_details value, no relabeling) — same
    // derivation as the original component's header summary.
    const itemTypeSummary = useMemo(() => {
        const counts = {};
        items.forEach((i) => {
            const type = i.item_type_details || 'unspecified';
            counts[type] = (counts[type] || 0) + (i.quantity || 1);
        });
        return Object.entries(counts)
            .map(([type, count]) => `${type}: ${count}`)
            .join('  •  ');
    }, [items]);

    const signature = order?.customer_signature;
    const signatureName = order?.customer_signature_meta?.name;

    return (
        <div>
            <SectionTitle first count={items.length}>
                Items
            </SectionTitle>
            {itemTypeSummary ? (
                <div
                    css={css`
                        font-family: ${FONT};
                        font-weight: 600;
                        font-size: 11px;
                        color: ${greys[1]};
                        padding: 2px 0 10px;
                    `}
                >
                    {itemTypeSummary}
                </div>
            ) : null}

            {items.length === 0 ? (
                <Empty>No items on this order.</Empty>
            ) : (
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 10px;
                    `}
                >
                    {items.map((i, idx) => (
                        <Card key={`item-${i.item_id || idx}`}>
                            <div
                                css={css`
                                    display: flex;
                                    align-items: center;
                                    gap: 8px;
                                    margin-bottom: 6px;
                                `}
                            >
                                <span
                                    css={css`
                                        font-family: ${FONT};
                                        font-weight: 800;
                                        font-size: 12px;
                                        color: ${onwardNavy};
                                    `}
                                >
                                    Line Item #{idx + 1}
                                </span>
                                <span
                                    css={css`
                                        margin-left: auto;
                                    `}
                                >
                                    <Pill color={ACCENT.green} background={SOFT.green}>
                                        Qty {i.quantity ?? '—'}
                                    </Pill>
                                </span>
                            </div>

                            <div
                                css={css`
                                    font-family: ${FONT};
                                    font-weight: 700;
                                    font-size: 12.5px;
                                    color: ${greys[3]};
                                    line-height: 1.4;
                                `}
                            >
                                {i.description || 'Description Not Provided'}
                            </div>

                            {i.sku || i.barcode ? (
                                <div
                                    css={css`
                                        display: flex;
                                        flex-wrap: wrap;
                                        gap: 6px;
                                        margin-top: 8px;
                                    `}
                                >
                                    {i.sku ? <Pill uppercase>SKU {i.sku}</Pill> : null}
                                    {i.barcode ? <Pill uppercase>Barcode {i.barcode}</Pill> : null}
                                </div>
                            ) : null}

                            <div
                                css={css`
                                    margin-top: 8px;
                                    border-top: 1px solid #f2f2f2;
                                    padding-top: 4px;
                                `}
                            >
                                <ItemMeta
                                    label="Dimensions"
                                    value={
                                        i.length ? `${i.length}L ${i.width}W ${i.height}H` : 'Dimensions Not Provided'
                                    }
                                />
                                <ItemMeta
                                    label="Total Cubes"
                                    value={i?.total_cubes != null ? i.total_cubes.toFixed(1) : '—'}
                                />
                                <ItemMeta label="Weight / item" value={i.weight ? `${i.weight} lbs` : '—'} />
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            <SectionTitle count={haulaway.length || null}>Haulaway</SectionTitle>
            {haulaway.length ? (
                <Card>
                    {haulaway.map((i, idx) => (
                        <div
                            key={`haulaway-${idx}`}
                            css={css`
                                font-family: ${FONT};
                                font-weight: 600;
                                font-size: 12px;
                                color: ${greys[3]};
                                padding: 3px 0;
                                ${idx > 0 ? 'border-top: 1px solid #f2f2f2;' : ''}
                            `}
                        >
                            {i.description || 'Description Not Provided'}
                        </div>
                    ))}
                </Card>
            ) : (
                <Empty>None</Empty>
            )}

            <PhotoSection title="Warehouse" urls={order?.warehouse_file || []} keyPrefix="warehouse" />
            <PhotoSection title="BOL" urls={order?.bol_file || []} keyPrefix="bol" />
            <PhotoSection title="Waybill" urls={order?.waybill || []} keyPrefix="waybill" />
            <PhotoSection
                title="Pickup Notification"
                urls={order?.pickup_notification || []}
                keyPrefix="pickup_notification"
            />
            <PhotoSection title="POD" urls={(order?.pod || []).map((p) => p.url)} keyPrefix="pod" />
            <PhotoSection
                title="Final Delivery Photos"
                urls={(order?.delivery_photo || []).map((p) => p.url)}
                keyPrefix="delivery_photo"
            />

            <SectionTitle>Customer Signature</SectionTitle>
            {signature || signatureName ? (
                <div>
                    {signature ? (
                        <a
                            href={signature}
                            target="_blank"
                            rel="noreferrer"
                            css={css`
                                display: inline-flex;
                                border: 1px solid ${greys[4]};
                                border-radius: 6px;
                                overflow: hidden;
                                background: #fff;
                                padding: 6px;
                            `}
                        >
                            <img
                                src={signature}
                                alt="Customer signature"
                                css={css`
                                    max-width: 100%;
                                    max-height: 120px;
                                    object-fit: contain;
                                `}
                            />
                        </a>
                    ) : null}
                    {signatureName ? (
                        <div
                            css={css`
                                font-family: ${FONT};
                                font-size: 12px;
                                color: ${greys[2]};
                                margin-top: ${signature ? '8px' : '2px'};
                            `}
                        >
                            Signature manually entered for{' '}
                            <span
                                css={css`
                                    font-weight: 700;
                                    color: ${greys[3]};
                                `}
                            >
                                {signatureName}
                            </span>
                        </div>
                    ) : null}
                </div>
            ) : (
                <Empty>No signature</Empty>
            )}
        </div>
    );
}
